import React, { useEffect, useRef } from "react";
import { 
  X, User, Mail, Phone, Briefcase, Calendar, Award, BookOpen, 
  Info, MapPin, Clock, FileText 
} from "lucide-react";

const Section = ({ title, children, icon }) => (
  <div className="border border-gray-200 rounded-xl mb-6 shadow-sm bg-white">
    <div className="flex items-center space-x-3 px-6 py-4 bg-gray-50 border-b border-gray-200 rounded-t-xl">
      <div className="p-2 bg-white rounded-lg shadow-sm">
        {icon}
      </div>
      <h3 className="font-semibold text-gray-800 text-lg">{title}</h3>
    </div>
    <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">{children}</div>
  </div>
);

const ReadOnlyField = ({ label, value, icon }) => (
  <div>
    <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
      {icon && <div className="p-1 bg-gray-100 rounded">{icon}</div>}
      <span>{label}</span>
    </label>
    <div className="relative">
      <div className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 bg-gray-100 text-gray-800 transition-all duration-200">
        {value || "N/A"}
      </div>
      {icon && (
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {icon}
        </div>
      )}
    </div>
  </div>
);

export default function ViewApplicantModal({ applicant, onClose }) {  // FIXED: Changed from closeModal to onClose
  const modalRef = useRef(null);
  
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Click outside to close - NEW FUNCTIONALITY
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Close on Escape key - NEW FUNCTIONALITY
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div 
          ref={modalRef}  // ADDED: Reference for click outside functionality
          className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden transform transition-all duration-300 ease-out"
        >
          {/* Enhanced Header with Orange Background */}
          <div className="flex-shrink-0 bg-gradient-to-r from-orange-200 to-orange-200 border-b border-orange-100">
            <div className="flex items-center justify-between px-8 py-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl shadow-sm">
                  <User className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-black">Applicant Details</h2>
                  <p className="text-lg text-black-100 mt-1">Recruitment Management</p>
                </div>
              </div>
              <button
                onClick={onClose}  // FIXED: Changed from closeModal to onClose
                className="p-2 rounded-xl transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white hover:shadow-sm"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Enhanced Body */}
          <div className="flex-grow px-8 py-6 overflow-y-auto">
            <Section title="Personal Information" icon={<User className="w-5 h-5 text-orange-500" />}>
              <ReadOnlyField 
                label="Full Name" 
                value={applicant.fullName} 
                icon={<User className="h-5 w-5 text-gray-400" />}
              />
              <ReadOnlyField 
                label="Email" 
                value={applicant.email} 
                icon={<Mail className="h-5 w-5 text-gray-400" />}
              />
              <ReadOnlyField 
                label="Phone" 
                value={applicant.phone} 
                icon={<Phone className="h-5 w-5 text-gray-400" />}
              />
              <ReadOnlyField 
                label="Source" 
                value={applicant.source} 
                icon={<Info className="h-5 w-5 text-gray-400" />}
              />
              <ReadOnlyField 
                label="Status" 
                value={applicant.status} 
                icon={<Award className="h-5 w-5 text-gray-400" />}
              />
            </Section>

            <Section title="Job Information" icon={<Briefcase className="w-5 h-5 text-orange-500" />}>
              <ReadOnlyField 
                label="Job Applied" 
                value={applicant.jobId?.vacancy} 
                icon={<Briefcase className="h-5 w-5 text-gray-400" />}
              />
              <ReadOnlyField 
                label="Skills" 
                value={applicant.skills} 
                icon={<Award className="h-5 w-5 text-gray-400" />}
              />
              <ReadOnlyField 
                label="Experience" 
                value={applicant.experience} 
                icon={<Clock className="h-5 w-5 text-gray-400" />}
              />
              <ReadOnlyField 
                label="Education" 
                value={applicant.education} 
                icon={<BookOpen className="h-5 w-5 text-gray-400" />}
              />
              <ReadOnlyField 
                label="Applied On" 
                value={formatDate(applicant.appliedOn)} 
                icon={<Calendar className="h-5 w-5 text-gray-400" />}
              />
            </Section>
          </div>

          {/* Enhanced Footer */}
          <div className="flex-shrink-0 bg-gray-50 px-8 py-6 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Info className="w-4 h-4 text-orange-500" />
                  <span>Viewing applicant information</span>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <button
                  type="button"
                  onClick={onClose}  // FIXED: Changed from closeModal to onClose
                  className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 text-gray-700 hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

